import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { isValueExist } from '@/utils/utils';
import * as S from './styles';

import WithPanel from '@components/Common/WithPanel';
import { PANEL_OPTION_KEYS } from '@consts';

import iframeMixin from '@/mixins/iframe';
import { useSearch } from '@/composable/useSearch';
import { useSecondPanel } from '@/composable/useSecondPanel';

import Switch from '@components/Common/Switch';
import RingLoader from '@components/Common/RingLoader';

import InfoIcon from '@svgIcons/info_line.svg';

import { getSelectedChannels } from '..';

export default {
    name: 'WidgetSettingPanelOptionsMixin',
    setup({ indexKey, compKey }) {
        const {
            isSecondPanelOpen,
            secondPanelVisibility$,
            closeSecondPanel,
            toggleSecondPanel,
        } = useSecondPanel({ key: compKey });

        const {
            value: searchText,
            filter,
            setValue: setSearchText,
            lazyValue: lazySeachText,
        } = useSearch({ lazy: true });

        return {
            isSecondPanelOpen,
            secondPanelVisibility$,
            closeSecondPanel,
            toggleSecondPanel,
            searchText,
            setSearchText,
            lazySeachText,
            filterList: filter(R.prop(indexKey)),
        };
    },
    mixins: [iframeMixin],
    props: {
        selectedList: {
            type: Array,
            default() {
                return [];
            },
        },
        currentList: {
            type: Array,
            default() {
                return [];
            },
        },
        isItemDisabled: {
            type: Function,
            default: R.F,
        },
        isItemSortable: {
            type: Boolean,
            default: true,
        },
        rawList: {
            type: Array,
            default() {
                return [];
            },
        },
        editingWidget: {
            type: Object,
            default() {
                return {};
            },
        },
        widgetType: String,
        compKey: String,
        currentListByType: {
            type: Function,
            default: R.always([]),
        },
        selectedListByType: {
            type: Function,
            default: R.always([]),
        },
        localeKey: String,
        translator: {
            type: Function,
            default: R.identity,
        },
        indexKey: {
            type: String,
        },
        panelLoading: {
            type: Boolean,
            default: false,
        },
        secondPanelLoading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hasEditBtn: {
            type: Boolean,
            default: true,
        },
        widgetDateFilter: {
            type: Object,
            default() {
                return {};
            },
        },
        widgetComparisonDateFilter: {
            type: Object,
            default() {
                return {};
            },
        },
        graphOptions: {
            type: Object,
            default() {
                return {};
            },
        },
        comparisonByDate: {
            type: Boolean,
            default: false,
        },
        columnDimensionIds: {
            type: Array,
            default() {
                return [];
            },
        },
        isTemplateView: Boolean,

        /**
         * availableFields props
         */
        selectedSource: {
            type: Array,
            default() {
                return [];
            },
        },
        currentBreakdownIds: {
            type: Array,
            default() {
                return [];
            },
        },
        isAnalytic: {
            type: Boolean,
            default: false,
        },
        currentViewId: Number | String,
        teamspaceId: Number,
    },
    watch: {
        shouldCloseAllSecondPanel(v) {
            if (!v) return;

            if (
                this.compKey === PANEL_OPTION_KEYS.MEMBER_SELECTOR &&
                typeof this.handleCloseMemberSelectorPanel === 'function'
            ) {
                this.handleCloseMemberSelectorPanel();
                return;
            }

            this.closeSecondPanel();
        },
    },
    computed: {
        ...mapState('newDashboard', ['shouldCloseAllSecondPanel']),
        ...mapGetters('newDashboard', ['dateFilterProps', 'viewById']),
        teamspace() {
            return this.viewById(this.teamspaceId);
        },
        assetIds() {
            return this.selectedSource.map((v) => v.assetId);
        },
        selectedChannels() {
            return getSelectedChannels(this.selectedSource);
        },
        fieldWidgetType() {
            return this.isAnalytic ? 'normal' : 'marketing';
        },
        isComparisonByDate() {
            return (
                this.comparisonByDate || isValueExist(this.columnDimensionIds)
            );
        },
    },
    methods: {
        ...mapMutations('view', ['setNotiMsgs']),
        ...mapActions('iframe', ['postIframeMessage']),
        wrapOptions({
            children,
            headerContent,
            tooltipTitle,
            selected,
            icon,
            title,
            hasEditBtn = true,
            disableClick = false,
            onClick,
            hasSwitch,
            isSwitchActive = false,
            switchDisabled = false,
            loading = false,
            openReactPanel,
        }) {
            const { isSecondPanelOpen, disabled } = this;

            return (
                <S.ListWrapper
                    selected={selected}
                    isHoverPointer={hasEditBtn}
                    onClick={() => {
                        if (disabled) return;
                        if (disableClick) return;
                        if (hasSwitch) return;

                        onClick && onClick();
                        openReactPanel && openReactPanel();

                        this.toggleSecondPanel();
                    }}
                    disabled={disabled}
                    class="option-wrapper"
                >
                    <S.TitleLabel
                        class="option-title"
                        selected={selected}
                        hideBorder={!isValueExist(children)}
                    >
                        <S.IconWrapper class="option-title-text">
                            {icon}
                            {title}
                        </S.IconWrapper>
                        {tooltipTitle &&
                            this.wrapTooltip({
                                visible: true,
                                content: (
                                    <S.InfoIconWrapper>
                                        <InfoIcon
                                            v-view-box
                                            class="info-line-icon"
                                        />
                                    </S.InfoIconWrapper>
                                ),
                                title: tooltipTitle,
                            })}
                        {isValueExist(headerContent) && headerContent}

                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                        >
                            {loading && <RingLoader size={16} />}
                            {hasEditBtn && (
                                <S.EditButton highlighted={!isSecondPanelOpen}>
                                    {this.$t('COMMON.edit')}
                                </S.EditButton>
                            )}
                        </div>

                        {hasSwitch && (
                            <Switch
                                value={isSwitchActive}
                                type="big"
                                onClick={() => {
                                    onClick && onClick();
                                }}
                                disabled={switchDisabled}
                            />
                        )}
                    </S.TitleLabel>
                    {isValueExist(children) && children}
                </S.ListWrapper>
            );
        },
        renderNoList(msg) {
            return <S.NoListSpan>+ {msg}</S.NoListSpan>;
        },
        renderSecondPanel({
            title,
            content,
            closeOutside,
            onClose,
            shouldActive,
            editAreaPadding,
        }) {
            if (!this.isSecondPanelOpen) return;

            const props = {
                attrs: {
                    renderPanel: this.isSecondPanelOpen,
                    title,
                    wrapStyle: { right: '545px' },
                    hasButtons: false,
                    closeOutside,
                    closeBlockClasses: ['adriel-dialog', 'ant-dropdown'],
                    loadingMask:
                        this.panelLoading ||
                        this.secondPanelLoading ||
                        this.compatibilityLoading,
                    editAreaPadding,
                },
                on: {
                    close: () => {
                        !shouldActive && this.closeSecondPanel();
                        onClose && onClose();
                    },
                },
            };
            return <WithPanel {...props}>{content}</WithPanel>;
        },
    },
};
